import * as React from 'react'

import {
  IWorkflowDurationOptions,
  WorkflowDurationType,
  WorkflowDurationUnit,
} from '~/client/graph'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  MINUTES_IN_DAY,
  MINUTES_IN_HOUR,
  MINUTES_IN_MONTH,
  MINUTES_IN_WEEK,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import FormGeneralOptionsDropdown, {
  IFormGeneralTabDropdownOption,
} from './FormGeneralOptionsDropdown'

interface IProps {
  durationOptions: IWorkflowDurationOptions
  switchLabel: string
  durationLabel: string
  durationTimeLabel: string
  maximumDuration?: number
  onChange: (options: Partial<IWorkflowDurationOptions>) => void
}

const durationTypeOptions: IFormGeneralTabDropdownOption[] = [
  {
    id: WorkflowDurationType.AllDay,
    name: Localization.translator.allDay,
  },
  {
    id: WorkflowDurationType.WorkWeek,
    name: Localization.translator.workWeek,
  },
  {
    id: WorkflowDurationType.Time,
    name: Localization.translator.time,
  },
]

export default class WorkflowDurationConfiguration extends React.Component<IProps> {
  public render() {
    const { durationOptions, switchLabel, durationTimeLabel, durationLabel } =
      this.props
    const { durationType, durationUnit, duration } = durationOptions

    return (
      <div className="py10">
        <div className="tw:flex tw:items-center tw:gap-2 tw:pb-2.5">
          <div className="text large line-extra-large tw:flex-1">
            {switchLabel}
          </div>
          <Switch
            checked={durationOptions?.isEnabled}
            onCheckedChange={this.handleDurationEnabledChange}
          />
        </div>
        {durationOptions?.isEnabled && (
          <>
            <FormGeneralOptionsDropdown
              targetClassName="full-width"
              customSelectorLabel={durationLabel}
              selectedValue={durationType}
              options={durationTypeOptions}
              onItemClick={this.handleDurationTypeChange}
            />

            {durationType === WorkflowDurationType.Time && (
              <div className="row gap-10">
                <StruxhubInput
                  type="number"
                  className="full-width"
                  isRequiredTextHidden
                  label={durationTimeLabel}
                  min={1}
                  isValid={duration <= this.getMaxDuration()}
                  validationMessage={this.maxDurationMessage}
                  value={duration?.toString() || EMPTY_STRING}
                  onChange={this.handleDurationChange}
                />

                <FormGeneralOptionsDropdown
                  itemRowClassName="large"
                  targetClassName="pt20"
                  selectedValue={durationUnit.toString()}
                  options={this.durationUnitOptions}
                  onItemClick={this.handleDurationUnitChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  private get maxDurationMessage(): string {
    const { durationUnit } = this.props.durationOptions

    const message = Localization.translator.durationCannotBeGreaterThan

    switch (durationUnit) {
      case WorkflowDurationUnit.Minutes:
        return `${message} ${Localization.translator.xMinutes(
          this.getMaxDuration(),
        )}`
      case WorkflowDurationUnit.Hours:
        return `${message} ${Localization.translator.xHours(
          this.getMaxDuration(),
        )}`
      case WorkflowDurationUnit.Days:
        return `${message} ${Localization.translator.xDays(
          this.getMaxDuration(),
        )}`
      case WorkflowDurationUnit.Weeks:
        return `${message} ${Localization.translator.xWeeks(
          this.getMaxDuration(),
        )}`
      case WorkflowDurationUnit.Months:
        return `${message} ${Localization.translator.xMonths(
          this.getMaxDuration(),
        )}`
    }
  }

  private getMaxDuration(): number {
    const { durationOptions, maximumDuration } = this.props
    const { durationUnit } = durationOptions

    if (!maximumDuration) {
      return Infinity
    }

    switch (durationUnit) {
      case WorkflowDurationUnit.Minutes:
        return maximumDuration
      case WorkflowDurationUnit.Hours:
        return maximumDuration / MINUTES_IN_HOUR
      case WorkflowDurationUnit.Days:
        return maximumDuration / MINUTES_IN_DAY
      case WorkflowDurationUnit.Weeks:
        return maximumDuration / MINUTES_IN_WEEK
      case WorkflowDurationUnit.Months:
        return maximumDuration / MINUTES_IN_MONTH
    }
  }

  private get durationUnitOptions(): IFormGeneralTabDropdownOption[] {
    const { duration } = this.props.durationOptions
    return [
      {
        id: WorkflowDurationUnit.Minutes,
        name: Localization.translator.minute_plural(duration),
      },
      {
        id: WorkflowDurationUnit.Hours,
        name: Localization.translator.hour_plural(duration),
      },
      {
        id: WorkflowDurationUnit.Days,
        name: Localization.translator.day_plural(duration),
      },
      {
        id: WorkflowDurationUnit.Weeks,
        name: Localization.translator.week_plural(duration),
      },
      {
        id: WorkflowDurationUnit.Months,
        name: Localization.translator.month_plural(duration),
      },
    ]
  }

  private handleDurationEnabledChange = () => {
    this.props.onChange({
      isEnabled: !this.props.durationOptions.isEnabled,
    })
  }

  private handleDurationTypeChange = (durationType: string) => {
    this.props.onChange({
      durationType: durationType as WorkflowDurationType,
    })
  }

  private handleDurationUnitChange = (durationUnit: string) => {
    this.props.onChange({
      durationUnit: durationUnit as WorkflowDurationUnit,
    })
  }

  private handleDurationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    this.props.onChange({
      duration: Number(event.target.value || 0),
    })
  }
}
